import React, { useState, useEffect, useCallback } from 'react';
import { amDesignColors, amDesignTypography, amDesignComponents } from '../../themes/amDesignSystem';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Pagination,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Avatar,
  Divider,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Build as ServiceIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { useBusiness } from '../../contexts/BusinessContext';
import axios from 'axios';

const ServiceActivityLog = ({ googleLocationId }) => {
  const { selectedBusiness } = useBusiness();
  const [activities, setActivities] = useState([]);
  const [summary, setSummary] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [filterType, setFilterType] = useState('all');
  const [viewMode, setViewMode] = useState('activities'); // 'activities' or 'services'

  const locationId = googleLocationId || selectedBusiness?.googleLocationId;

  // Fetch activities
  const fetchActivities = useCallback(async () => {
    if (!locationId) {
      setError('No location selected');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log(`📋 Fetching activities for location: ${locationId}`);

      const skip = (page - 1) * limit;
      const response = await axios.get(
        `/api/activities/${locationId}`,
        {
          params: { limit, skip }
        }
      );

      if (response.data.success) {
        setActivities(response.data.data);
        setTotal(response.data.pagination.total);
      } else {
        setError('Failed to fetch activities');
      }
    } catch (err) {
      console.error('❌ Error fetching activities:', err);
      setError(err.response?.data?.error || 'Failed to fetch activities');
    } finally {
      setLoading(false);
    }
  }, [locationId, page, limit]);

  // Fetch activity summary
  const fetchSummary = useCallback(async () => {
    if (!locationId) return;

    try {
      console.log(`📊 Fetching activity summary for location: ${locationId}`);

      const response = await axios.get(
        `/api/activities/${locationId}/summary`
      );

      if (response.data.success) {
        setSummary(response.data.data);
      }
    } catch (err) {
      console.error('❌ Error fetching summary:', err);
    }
  }, [locationId]);

  // Fetch current services
  const fetchServices = useCallback(async () => {
    if (!locationId) return;

    try {
      console.log(`📋 Fetching services for location: ${locationId}`);

      const response = await axios.get(
        `/api/services/${locationId}`
      );

      if (response.data.success) {
        const freeFormServices = response.data.freeFormServiceItems || [];
        const structuredServices = response.data.structuredServiceItems || [];

        // Combine and format services
        const allServices = [
          ...freeFormServices.map(service => ({
            id: service.label?.displayName || 'Unknown',
            name: service.label?.displayName || 'Unknown',
            type: 'freeForm',
            category: service.category,
            description: service.label?.description || '',
            status: 'active'
          })),
          ...structuredServices.map(service => ({
            id: service.serviceTypeId,
            name: service.serviceTypeId,
            type: 'structured',
            category: service.serviceTypeId,
            description: service.description || '',
            status: 'active'
          }))
        ];

        setServices(allServices);
      }
    } catch (err) {
      console.error('❌ Error fetching services:', err);
    }
  }, [locationId]);

  // Effect: Fetch on mount and when location/pagination changes
  useEffect(() => {
    if (viewMode === 'activities') {
      fetchActivities();
    } else {
      fetchServices();
    }
    fetchSummary();
  }, [fetchActivities, fetchSummary, fetchServices, viewMode]);

  // Get activity icon
  const getActivityIcon = (activityType) => {
    switch (activityType) {
      case 'service_added':
        return <AddIcon fontSize="small" sx={{ color: amDesignColors.success }} />;
      case 'service_deleted':
        return <DeleteIcon fontSize="small" sx={{ color: amDesignColors.error }} />;
      case 'service_description_added':
      case 'service_description_updated':
        return <DescriptionIcon fontSize="small" sx={{ color: amDesignColors.primary }} />;
      case 'service_updated':
        return <EditIcon fontSize="small" sx={{ color: amDesignColors.warning }} />;
      default:
        return <InfoIcon fontSize="small" sx={{ color: amDesignColors.text.tertiary }} />;
    }
  };

  // Get activity label
  const getActivityLabel = (activityType) => {
    switch (activityType) {
      case 'service_added':
        return 'Service Added';
      case 'service_deleted':
        return 'Service Deleted';
      case 'service_description_added':
        return 'Description Added';
      case 'service_description_updated':
        return 'Description Updated';
      case 'service_updated':
        return 'Service Updated';
      default:
        return activityType;
    }
  };

  // Get activity color
  const getActivityColor = (activityType) => {
    switch (activityType) {
      case 'service_added':
        return 'success';
      case 'service_deleted':
        return 'error';
      case 'service_description_added':
      case 'service_description_updated':
        return 'info';
      case 'service_updated':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Filter activities
  const filteredActivities = filterType === 'all'
    ? activities
    : activities.filter(a => a.activityType === filterType);

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!locationId) {
    return (
      <Card>
        <CardContent>
          <Alert severity="warning">No business location selected</Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Debug Info */}
      <Alert severity="info" sx={{ mb: 2 }}>
        📍 Location: {locationId} | Activities: {activities.length} | Filtered: {filteredActivities.length}
      </Alert>

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Services Added
                </Typography>
                <Typography variant="h5">
                  {summary.servicesAdded}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Descriptions Added/Updated
                </Typography>
                <Typography variant="h5">
                  {summary.descriptionsAdded}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Activities
                </Typography>
                <Typography variant="h5">
                  {total}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* View Mode Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={viewMode === 'activities' ? 0 : 1}
          onChange={(e, newValue) => {
            setViewMode(newValue === 0 ? 'activities' : 'services');
            setPage(1);
            setFilterType('all');
          }}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab
            label="Aktivitätsverlauf"
            icon={<HistoryIcon />}
            iconPosition="start"
          />
          <Tab
            label="Aktuelle Dienste"
            icon={<ServiceIcon />}
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Activity List */}
      {viewMode === 'activities' ? (
      <>
      {/* Activity Filter */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Aktivitätstyp</InputLabel>
                <Select
                  value={filterType}
                  onChange={(e) => {
                    setFilterType(e.target.value);
                    setPage(1);
                  }}
                  label="Aktivitätstyp"
                >
                  <MenuItem value="all">Alle Aktivitäten</MenuItem>
                  <MenuItem value="service_added">Dienst hinzugefügt</MenuItem>
                  <MenuItem value="service_deleted">Dienst gelöscht</MenuItem>
                  <MenuItem value="service_description_added">Beschreibung hinzugefügt</MenuItem>
                  <MenuItem value="service_description_updated">Beschreibung aktualisiert</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          title="Service Activity Log"
          subheader="Track all changes made to services and descriptions"
        />
        <Divider />
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">
              Error: {error}
              <br />
              <small>Location ID: {locationId}</small>
            </Alert>
          ) : filteredActivities.length === 0 ? (
            <>
              <Alert severity="info">
                No activities found yet
                <br />
                <small>Location ID: {locationId} | Total activities: {total} | Activities array: {activities.length}</small>
              </Alert>
              <Typography variant="body2" sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                💡 <strong>Tip:</strong> Add a new service from the Service Management page to see it here!
              </Typography>
            </>
          ) : (
            <>
              <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table sx={{ minWidth: 900 }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Date & Time</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Activity</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '35%' }}>Services</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '35%' }}>Changes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredActivities.map((activity, index) => (
                      <TableRow key={activity._id || index} hover>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(activity.timestamp)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            {getActivityIcon(activity.activityType)}
                            <Chip
                              label={getActivityLabel(activity.activityType)}
                              color={getActivityColor(activity.activityType)}
                              size="small"
                              variant="outlined"
                            />
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              📋 {activity.serviceName}
                            </Typography>
                            {activity.serviceDescription && (
                              <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                                "{activity.serviceDescription.substring(0, 80)}{activity.serviceDescription.length > 80 ? '...' : ''}"
                              </Typography>
                            )}
                            <Typography variant="caption" color="textSecondary">
                              Category: {activity.serviceCategory || 'N/A'}
                            </Typography>
                            <Stack direction="row" spacing={0.5}>
                              <Chip
                                label={activity.serviceType === 'freeForm' ? 'Free-Form' : 'Structured'}
                                size="small"
                                variant="outlined"
                                color={activity.serviceType === 'freeForm' ? 'success' : 'primary'}
                                sx={{ width: 'fit-content' }}
                              />
                              {activity.isOptimized && (
                                <Chip
                                  label="KI optimiert"
                                  size="small"
                                  variant="filled"
                                  color="info"
                                  sx={{ width: 'fit-content' }}
                                />
                              )}
                            </Stack>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={1}>
                            {activity.activityType === 'service_added' && (
                              <Typography variant="caption" color="success.main" sx={{ fontWeight: 500 }}>
                                ✅ Service Added
                              </Typography>
                            )}
                            {activity.activityType === 'service_deleted' && (
                              <Typography variant="caption" color="error" sx={{ fontWeight: 500 }}>
                                ❌ Service Deleted
                              </Typography>
                            )}
                            {(activity.activityType === 'service_description_added' || activity.activityType === 'service_description_updated') && (
                              <>
                                {activity.previousDescription && (
                                  <Typography variant="caption" color="error">
                                    Was: {activity.previousDescription.substring(0, 60)}
                                    {activity.previousDescription.length > 60 ? '...' : ''}
                                  </Typography>
                                )}
                                {activity.newDescription && (
                                  <Typography variant="caption" color="success.main">
                                    Now: {activity.newDescription.substring(0, 60)}
                                    {activity.newDescription.length > 60 ? '...' : ''}
                                  </Typography>
                                )}
                                {!activity.previousDescription && activity.newDescription && (
                                  <Typography variant="caption" color="success.main" sx={{ fontWeight: 500 }}>
                                    Description Added
                                  </Typography>
                                )}
                              </>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={Math.ceil(total / limit)}
                  page={page}
                  onChange={(e, newPage) => setPage(newPage)}
                  color="primary"
                />
              </Box>
            </>
          )}
        </CardContent>
      </Card>
      </>
      ) : (
      /* Services View */
      <Card>
        <CardHeader
          title="Current Services"
          subheader="All services currently configured for this business"
        />
        <Divider />
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : services.length === 0 ? (
            <Alert severity="info">No services found for this business</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Service Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {services.map((service, index) => (
                    <TableRow key={service.id || index} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {service.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={service.type === 'freeForm' ? 'Free-Form' : 'Structured'}
                          color={service.type === 'freeForm' ? 'success' : 'primary'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {service.category}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {service.description ? (
                          <Typography variant="body2" sx={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {service.description}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                            No description
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label="Aktiv"
                          icon={<CheckCircleIcon />}
                          color="success"
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
      )}
    </Box>
  );
};

export default ServiceActivityLog;
